<template>
    <div class="container">
        <h1 class="title is-1">Material Estimator</h1>
        <div class="columns estimator">
            <div class="column is-half-tablet is-one-third-desktop">
                <h1 class="title">Project Details</h1>

                <div class="field">
                    <label class="label">Material Type</label>
                    <p class="control">
                        <span class="select">
                            <select :value="selectedMaterial" @change="selectedMaterial = $event.target.value">
                                <option v-for="(material, index) in materials" :value="index">{{ material.name
                                    }}</option>
                            </select>
                        </span>
                    </p>
                </div>

                <div class="field">
                    <label class="label">Area Shape</label>
                    <p class="control">
                        <span class="select">
                            <select :value="selectedArea" @change="selectedArea = $event.target.value">
                                <option v-for="(area, index) in areas" :value="index">{{ area.name }}</option>
                            </select>
                        </span>
                    </p>
                </div>

                <label class="label">Area Dimensions</label>
                <div v-for="(dimension, index) in areaDimensions">
                    <label class="label dimension-label">{{ dimension.name }}</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="text" v-model="dimensions[index].value"
                                   :placeholder="dimension.name">
                        </p>
                        <p class="control">
                            <span class="select">
                                <select :value="dimensions[index].unit"
                                        @change="dimensions[index].unit = $event.target.value">
                                    <option v-for="(unit, index) in units" :value="index">{{ unit.name }}</option>
                                </select>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
            <div class="column">
                <h1 class="title">{{ materials[selectedMaterial].name }} Estimate:</h1>
                <div class="content">
                    <div class="is-large results">
                        Your project has an area of <strong>{{ Math.round((results.area / 12 / 108) * precision) / precision
                        }}</strong> square yards.
                        <br/>
                        You will need roughly
                        <strong>{{ results.yards }}</strong>
                        cubic yards of {{ materials[selectedMaterial].name }}.
                        <br/>
                        This weighs about
                        <strong>{{ Math.round((results.yards * materials[selectedMaterial].poundsPerYard / 2000) * precision) / precision
                            }}</strong> tons
                        (or <strong>{{ Math.round((results.yards * materials[selectedMaterial].poundsPerYard) * precision) / precision
                        }}</strong> pounds).

                        <div class="area-shape">
                            <img src="../assets/shapes/rectangle.svg" v-if="selectedArea === 'rectangle'">
                            <img src="../assets/shapes/rectangleborder.svg" v-if="selectedArea === 'rectangleborder'">
                            <img src="../assets/shapes/circle.svg" v-if="selectedArea === 'circle'">
                            <img src="../assets/shapes/triangle.svg" v-if="selectedArea === 'triangle'">
                            <img src="../assets/shapes/circleborder.svg" v-if="selectedArea === 'circleborder'">
                        </div>
                    </div>
                    <br/>
                    <em>
                        <strong>Please note:</strong>
                        <br/>
                        This is an estimate planning tool <u>only</u>
                        and the real world values required for your project may
                        differ from the values determined here. Material weight can vary according to the batch, moisture conditions,
                        and other variables not accounted for in this estimate. Always be sure to double check your measurements
                        and err on the side of more material rather than less before starting your project.
                    </em>
                </div>
            </div>
        </div>
        <hr/>
        <h2 class="title">Material weight reference (per cubic yard)</h2>
        <div class="columns">
            <div class="column is-6">

                <table class="table">
                    <tbody>
                    <tr>
                        <td>Bark Mulch *</td>
                        <td>600-800 Lbs.</td>
                    </tr>
                    <tr>
                        <td>Cypress Mulch *</td>
                        <td>600-800 Lbs.</td>
                    </tr>
                    <tr>
                        <td>Soil Mixes *</td>
                        <td>1300-1650 Lbs.</td>
                    </tr>
                    <tr>
                        <td>Gravel *</td>
                        <td>2500-2600 Lbs.</td>
                    </tr>
                    <tr>
                        <td>Decomposed Granite *</td>
                        <td>2600 Lbs.</td>
                    </tr>
                    <tr>
                        <td>Sand *</td>
                        <td>2800 Lbs.</td>
                    </tr>
                    </tbody>
                </table>
                <em>* weight varies by moisture content and grain or chip size</em>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'calculators',
        data () {
            return {
                precision: 1000,
                selectedArea: 'rectangle',
                selectedMaterial: 'mulch',
                selectedResultVolumeType: 'yards',
                selectedResultVolumeType: 'yard',
                areas: {
                    rectangle: {
                        name: 'Rectangle',
                        dimensions: ['length', 'width', 'depth'],
                    },
                    rectangleborder: {
                        name: 'Rectangle Border',
                        dimensions: ['innerlength', 'innerwidth', 'borderwidth', 'depth'],
                    },
                    circle: {
                        name: 'Circle',
                        dimensions: ['diameter', 'depth'],
                    },
                    circleborder: {
                        name: 'Circle Border',
                        dimensions: ['innerdiameter', 'borderwidth', 'depth'],
                    },
                    triangle: {
                        name: 'Triangle',
                        dimensions: ['side1', 'side2', 'side3', 'depth'],
                    },
                },
                dimensions: {
                    side1: {
                        name: 'Side a Length',
                        unit: 'foot',
                        value: 1,
                    },
                    side2: {
                        name: 'Side b Length',
                        unit: 'foot',
                        value: 1,
                    },
                    side3: {
                        name: 'Side c Length',
                        unit: 'foot',
                        value: 1,
                    },
                    length: {
                        name: 'Length',
                        unit: 'foot',
                        value: 1,
                    },
                    width: {
                        name: 'Width',
                        unit: 'foot',
                        value: 1,
                    },
                    innerlength: {
                        name: 'Inner Length',
                        unit: 'foot',
                        value: 1,
                    },
                    innerwidth: {
                        name: 'Inner Width',
                        unit: 'foot',
                        value: 1,
                    },
                    diameter: {
                        name: 'Diameter',
                        unit: 'foot',
                        value: 1,
                    },
                    innerdiameter: {
                        name: 'Inner Diameter',
                        unit: 'foot',
                        value: 1,
                    },
                    borderwidth: {
                        name: 'Border Width',
                        unit: 'foot',
                        value: 1,
                    },
                    depth: {
                        name: 'Material Depth',
                        unit: 'inch',
                        value: 1,
                    },
                },
                units: {
                    inch: {
                        name: 'Inches',
                        unit: 1,
                    },
                    foot: {
                        name: 'Feet',
                        unit: 12,
                    },
                    yard: {
                        name: 'Yards',
                        unit: 36,
                    },
                },
                materials: {
                    mulch: {
                        name: 'Mulch',
                        poundsPerYard: 700,
                        volumeUnit: 'feet',
                        weightUnit: 'tons',
                    },
                    gravel: {
                        name: 'Gravel',
                        poundsPerYard: 2500,
                        volumeUnit: 'yards',
                        weightUnit: 'tons',
                    },
                    granite: {
                        name: 'Decomposed Granite',
                        poundsPerYard: 2600,
                        volumeUnit: 'yards',
                        weightUnit: 'tons',
                    },
                    soil: {
                        name: 'Soil',
                        poundsPerYard: 1500,
                        volumeUnit: 'yards',
                        weightUnit: 'tons',
                    },
                    sand: {
                        name: 'Sand',
                        poundsPerYard: 2800,
                        volumeUnit: 'yards',
                        weightUnit: 'tons',
                    }
                }
            }
        },
        computed: {
            areaShape () {
                return this.areas[this.selectedArea]
            },
            areaDimensions () {
                let desiredDimensions = this.areas[this.selectedArea].dimensions
                let dimensions = {}
                for (let newDimension in desiredDimensions) {
                    dimensions[desiredDimensions[newDimension]] = this.dimensions[desiredDimensions[newDimension]]
                }
                return dimensions
            },
            results() {
                let area = 0
                if (this.selectedArea === 'rectangle') {
                    let width = (this.dimensions.width.value * this.units[this.dimensions.width.unit].unit);
                    let length = (this.dimensions.length.value * this.units[this.dimensions.length.unit].unit);
                    area = width * length;
                } else if (this.selectedArea === 'triangle') {
                    let side1 = (this.dimensions.side1.value * this.units[this.dimensions.side1.unit].unit);
                    let side2 = (this.dimensions.side2.value * this.units[this.dimensions.side2.unit].unit);
                    let side3 = (this.dimensions.side3.value * this.units[this.dimensions.side3.unit].unit);
                    area = 0.25 * Math.sqrt(Math.max((side1 + side2 + side3), 0) *
                            Math.max((side2 + side3 - side1), 0) *
                            Math.max((side3 + side1 - side2), 0) *
                            Math.max((side1 + side2 - side3), 0))
                } else if (this.selectedArea === 'circle') {
                    let diameter = (this.dimensions.diameter.value * this.units[this.dimensions.diameter.unit].unit)
                    area = Math.PI * Math.pow(diameter / 2, 2)
                } else if (this.selectedArea === 'circleborder') {
                    let innerDiameter = (this.dimensions.innerdiameter.value * this.units[this.dimensions.innerdiameter.unit].unit)
                    let outerDiameter = innerDiameter + (2 * (this.dimensions.borderwidth.value * this.units[this.dimensions.borderwidth.unit].unit))
                    let outerArea = Math.PI * Math.pow(outerDiameter / 2, 2)
                    let innerArea = Math.PI * Math.pow(innerDiameter / 2, 2)
                    area = outerArea - innerArea
                } else if (this.selectedArea === 'rectangleborder') {
                    let width = (this.dimensions.innerwidth.value * this.units[this.dimensions.innerwidth.unit].unit);
                    let length = (this.dimensions.innerlength.value * this.units[this.dimensions.innerlength.unit].unit);
                    let borderwidth = (this.dimensions.borderwidth.value * this.units[this.dimensions.borderwidth.unit].unit);
                    let innerArea = width * length
                    let outerArea = (width + (borderwidth * 2)) * (length + (borderwidth * 2))
                    area = outerArea - innerArea
                }
                let depth = (this.dimensions.depth.value * this.units[this.dimensions.depth.unit].unit);
                let volume = area * depth
                let cubicInches = volume
                let cubicFeet = (volume / 1728)
                let cubicYards = cubicFeet / 27
                return {
                    area: area,
                    volume: volume,
                    feet: Math.round(cubicFeet * this.precision) / this.precision,
                    yards: Math.round(cubicYards * this.precision) / this.precision,
                }
            }
        },
    }
</script>



// WEBPACK FOOTER //
// Calculator.vue?570c5d6f